import { Fragment, useEffect, useState } from 'react'

import cloneDeep from 'lodash/cloneDeep'
import { useMeasure } from 'react-use'

import CategoryTextNodes from '@data/i18n/category.json'

import type {
	FilterValue,
	FilterValuePrice,
	FilterValues,
	Filters,
	SortByOptions
} from '@lib/types'

import SortProducts from '../SortProducts'
import CheckboxDropdown from './CheckboxDropdown'
import RangeSliderDropdown from './RangeSliderDropdown'
import StarsFilter from './StarsFilter'

type Props = {
	filters: FilterValues
	setFilters: (newFilters: FilterValues) => void
	filtersOptions: Filters
	isMobileFiltersOpen: boolean
	closeMobileFilters: () => void
	sortByOptions: SortByOptions
	setSortByOptions: (sortByOptions: SortByOptions) => void
	filterMenuOptions?: { title: string; path: string; active: boolean }[]
}

export default function RefineResults({
	filters,
	setFilters,
	filtersOptions,
	isMobileFiltersOpen,
	closeMobileFilters,
	sortByOptions,
	setSortByOptions,
	filterMenuOptions
}: Props) {
	const [clearFiltersKey, setClearFiltersKey] = useState<number>()

	const handlePriceFilter = (min: number, max: number, filter?: FilterValue) => {
		const newPriceFilter = { ...filter, input: { price: { min, max } } } as FilterValue

		if (filters.length) {
			// query already has a price filter
			const currentFilters = cloneDeep(filters) as FilterValues

			let hasPriceFilter = false

			// replace current price filter since only one can be applied per query
			for (const [i, currentFilter] of currentFilters.entries()) {
				if ((currentFilter.input as FilterValuePrice).price) {
					currentFilters[i] = newPriceFilter
					hasPriceFilter = true
					break
				}
			}

			if (!hasPriceFilter) {
				currentFilters.push(newPriceFilter)
			}

			setFilters(currentFilters)
		} else {
			setFilters([newPriceFilter])
		}
	}

	const handleCheckboxFilter = (checked: boolean, newFilter: FilterValue) => {
		if (filters.length) {
			// query already has a price filter
			let newFilters = cloneDeep(filters) as FilterValues

			if (checked) newFilters.push(newFilter)
			else newFilters = newFilters.filter((filter) => filter.id !== newFilter.id)

			setFilters(newFilters)
		} else {
			setFilters([newFilter])
		}
	}

	const clearFilters = () => {
		setFilters([])
		setClearFiltersKey(Date.now()) // use date as unique key
	}

	// Keyboard event listener to close modal + handle body element overflow
	useEffect(() => {
		const closeCurrentModal = (e: KeyboardEvent) =>
			e.key === 'Escape' ? closeMobileFilters() : null

		if (isMobileFiltersOpen) {
			window.addEventListener('keydown', closeCurrentModal)
			document.body.style.overflowY = 'hidden'
		}

		return () => {
			window.removeEventListener('keydown', closeCurrentModal)
			document.body.style.overflowY = 'visible'
		}
	}, [isMobileFiltersOpen])

	const [containerRef, { width: containerWidth }] = useMeasure<HTMLDivElement>()

	// Close modal when resizing to desktop
	useEffect(() => {
		if (containerWidth === 0) {
			closeMobileFilters()
		}
	}, [containerWidth])

	// A11Y: Changed from <aside> to <div> to avoid landmark nesting issues
	// The aside was contained within another landmark, which fails WCAG best practices
	return (
		<div
			role="region"
			aria-label="Filter products"
			className={`fixed inset-0 max-lg:pb-6 max-lg:overflow-y-auto z-10 max-lg:z-30 w-screen lg:w-64 transition-transform duration-500 ease-in-out bg-white shrink-0 lg:sticky lg:top-[120px] ${
				isMobileFiltersOpen ? 'translate-x-0' : 'max-lg:translate-x-full'
			}`}
		>
			{/* Mobile header */}
			<div className="py-6 border-b lg:hidden border-gray-200">
				<div className="container flex items-center justify-between" ref={containerRef}>
					<h2 className="text-lg font-medium">{CategoryTextNodes.filter.filterResults}</h2>

					<button
						type="button"
						className="p-2 text-lg"
						aria-label="Close"
						onClick={closeMobileFilters}
					>
						<i className="fa-light fa-xmark" />
					</button>
				</div>
			</div>

			<div className="max-lg:container max-lg:pt-6">
				{filterMenuOptions && (
					<div className="pb-10">
						<ul className="text-base font-semibold">
							{filterMenuOptions.map((filterMenuOption, idx) => (
								<li
									// eslint-disable-next-line react/no-array-index-key
									key={idx}
									className={`pb-2 ${filterMenuOption.active ? 'text-bbx-spark underline' : ''}`}
								>
									<a href={filterMenuOption.path}>{filterMenuOption.title}</a>
								</li>
							))}
						</ul>
					</div>
				)}
				<div className="flex justify-between pb-3 border-b max-lg:flex-col lg:pb-5 border-gray-350">
					{/* A11Y: Changed from h3 to div to fix heading hierarchy (h1 → h3 skipped h2) */}
					{/* This is a visual label, not a document section heading */}
					<div className="text-xl font-medium max-lg:hidden">{CategoryTextNodes.filter.refine}</div>

					<button
						type="button"
						className="ml-auto text-sm font-medium text-gray-500"
						onClick={clearFilters}
					>
						{CategoryTextNodes.filter.clear}
					</button>

					<div className="flex justify-end mt-5 lg:hidden">
						<SortProducts sortByOptions={sortByOptions} setSortByOptions={setSortByOptions} />
					</div>
				</div>
			</div>

			<div
				className="divide-y divide-gray-350 max-lg:container"
				key={`clearFiltersKey-${clearFiltersKey}`}
			>
				{filtersOptions &&
					filtersOptions.length > 0 &&
					filtersOptions.map((filter) => (
						<Fragment key={filter.id}>
							{filter.type === 'LIST' &&
								(filter.id === 'filter.p.m.loox.avg_rating' ? (
									<StarsFilter filter={filter} filters={filters} setFilters={setFilters} />
								) : (
									<CheckboxDropdown
										field={filter.label}
										options={filter.values}
										onChangeHandler={handleCheckboxFilter}
									/>
								))}

							{filter.type === 'PRICE_RANGE' &&
								filter.values.map((value) => {
									const { price } = value.input as FilterValuePrice

									return (
										<RangeSliderDropdown
											key={filter.id}
											filter={value}
											field={filter.label}
											min={{ label: `$${price.min}`, value: price.min }}
											max={{ label: `$${price.max}`, value: price.max }}
											onChangeHandler={handlePriceFilter}
										/>
									)
								})}
						</Fragment>
					))}
			</div>
		</div>
	)
}
